import type { UserAccountState } from '@/features/identity';

/**
 * Stan konta → klucz dopisku przy nazwie; `null`, gdy nie ma o czym pisać.
 *
 * ⭐ **Jedno miejsce dla czterech ekranów** (drzewko, inspektor, sekcja własności, historia).
 * Rozpisane w każdym z nich rozjechałyby się przy pierwszej zmianie słownictwa — a wtedy ten
 * sam człowiek byłby na jednym ekranie „wyłączony", a na drugim „nieaktywny".
 *
 * ⚠ **Dwa różne dopiski, bo to dwa różne fakty:** konto założone importem CZEKA na wejście
 * i nikt go nie wyłączał; konto wyłączone to decyzja admina. Zaraz po pierwszym imporcie
 * pierwszy przypadek dotyczy prawie każdego właściciela, więc jeden napis byłby wtedy
 * nieprawdą w większości wierszy (słowo usera 2026-09-04).
 *
 * ⚠ `null` przychodzi dla **zespołów** (nie mają konta) i dla zdarzeń bez autora.
 * ⛔ Nie mylić z `'active'` — „nie dotyczy" i „żywe" to dwie różne odpowiedzi, ale dopisku
 * nie dostaje żadna z nich.
 */
export function accountStateKey(state: UserAccountState | null): string | null {
  switch (state) {
    case 'pending':
      return 'graph.owner.accountPending';
    case 'disabled':
      return 'graph.owner.accountOff';
    default:
      return null;
  }
}

/**
 * Klasa dla nazwy w danym widoku — `null`, gdy koncie nic nie dolega.
 *
 * ⚠⚠ **Dwa stany dostają DWA wyglądy** (znalezione okiem 2026-09-04). Przekreślenie czyta się
 * jako „tego już nie ma" i jest prawdziwe przy koncie **wyłączonym** — człowiek odszedł.
 * ⛔ Przy koncie **czekającym** byłoby kłamstwem: ta osoba niczego nie odwołała, po prostu
 * jeszcze nie weszła. Zostaje samo wyszarzenie, a różnicę niesie słowo obok (dec. 58).
 *
 * @param styles mapa klas modułu CSS wołającego
 * @param nazwa bazowa nazwa klasy „konto wyłączone" w tym module (wariant czekający to `<nazwa>Pending`)
 */
export function accountStateClass(
  state: UserAccountState | null,
  styles: Record<string, string | undefined>,
  nazwa: string,
): string | undefined {
  switch (state) {
    case 'pending':
      return styles[`${nazwa}Pending`];
    case 'disabled':
      return styles[nazwa];
    default:
      return undefined;
  }
}
